import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavbarComponent } from '../navbar/navbar.component';
import { HeaderComponent } from '../header/header.component';
import { Router } from '@angular/router';
import { SessionService } from '../core/session.service';
import { AuthService } from '../core/auth.service';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { UiStateService } from '../shared/ui-state.service';

type PersonaKey = 'Admin' | 'Grower' | 'Transloader' | 'Scheduler' | 'Shunting' | 'Receiver' | 'Gatehouse' | 'Audit';

interface PersonaOption {
  id: PersonaKey;
  label: string;
  icon: string;
  iconSelected: string;
}

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, NavbarComponent, HeaderComponent],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.css'
})
export class ProfileComponent {
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly searchService = inject(SearchService);
  private readonly filterService = inject(FilterService);
  private readonly uiState = inject(UiStateService);

  readonly name = computed(() => this.session.displayName() || '');
  readonly email = computed(() => this.session.email() || '');
  readonly userRoles = computed(() => {
    const roles = this.session.personas() || [];
    return roles;
  });

  // Catalog of all personas with icons
  private readonly allPersonaCatalog: Record<string, PersonaOption> = {
    'admin':        { id: 'Admin', label: 'Admin', icon: '/assets/images/Admin.png', iconSelected: '/assets/images/Admin_Selected.png' },
    'grower':       { id: 'Grower', label: 'Grower', icon: '/assets/images/Grower.png', iconSelected: '/assets/images/Grower_Selected.png' },
    'transloader':  { id: 'Transloader', label: 'Transloader', icon: '/assets/images/Transloader.png', iconSelected: '/assets/images/Transloader_Selected.png' },
    'scheduler':    { id: 'Scheduler', label: 'Scheduler', icon: '/assets/images/Scheduler.png', iconSelected: '/assets/images/Scheduler_Selected.png' },
    'shunting':     { id: 'Shunting', label: 'Shunting', icon: '/assets/images/Shunting.png', iconSelected: '/assets/images/Shunting_Selected.png' },
    'receiver':     { id: 'Receiver', label: 'Receiver', icon: '/assets/images/Receiver.png', iconSelected: '/assets/images/Receiver_Selected.png' },
    'gatehouse':    { id: 'Gatehouse', label: 'Gatehouse', icon: '/assets/images/GateHouse.png', iconSelected: '/assets/images/GateHouse_Selected.png' },
    'audit':        { id: 'Audit', label: 'Audit', icon: '/assets/images/Audit.png', iconSelected: '/assets/images/Audit_Selected.png' }
  };

  // Compute visible persona options based on roles from session
  readonly personas = computed<PersonaOption[]>(() => {
    const roles = (this.userRoles() || []).map(r => (r || '').toLowerCase());
    const unique = Array.from(new Set(roles));
    const mapped: PersonaOption[] = [];
    for (const role of unique) {
      const key = role.replaceAll(/\s+/g, '');
      const option = this.allPersonaCatalog[key];
      if (option) mapped.push(option);
    }
    // Fallback to selected persona if no roles found
    if (mapped.length === 0) {
      const selectedPersonaValue = this.selectedPersona();
      if (selectedPersonaValue) {
        const selected = selectedPersonaValue.toLowerCase().replaceAll(/\s+/g, '');
        const fallbackOption = this.allPersonaCatalog[selected];
        if (fallbackOption) {
          mapped.push(fallbackOption);
        }
      }
    }
    return mapped;
  });

  readonly selectedPersona = computed<string | null>(() => {
    const persona = this.session.persona();
    return persona || null;
  });

  readonly initials = computed(() => {
    const source = this.name() || this.email() || '';
    const parts = source.split(/[\s.@_]+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? parts.at(-1)?.charAt(0) ?? '' : '';
    const merged = (first + last).toUpperCase();
    if (merged) return merged;
    const fallback = source.charAt(0).toUpperCase();
    return /[A-Z0-9]/.test(fallback) ? fallback : '';
  });

  onSelectPersona(personaId: string): void {
    this.session.setPersona(personaId);
    
    // Clear search and filters when switching personas
    console.log('🔄 Persona changed - clearing search and filters');
    this.searchService.clear();
    this.filterService.clear();
    
    // Close any open search/filter panels
    this.uiState.setSearchOpen(false);
    this.uiState.setFiltersOpen(false);
    
    // Navigate to the selected persona's homepage
    const p = (personaId || '').toLowerCase();
    
    // Route to appropriate page based on persona
    if (p.includes('admin')) {
      this.router.navigateByUrl('/admin');
      return;
    }
    if (p.includes('grower')) {
      this.router.navigateByUrl('/growers');
      return;
    }
    if (p.includes('transload')) {
      this.router.navigateByUrl('/transloading');
      return;
    }
    if (p.includes('receiv')) {
      this.router.navigateByUrl('/receiver');
      return;
    }
    if (p.includes('shunting') || p.includes('shunt')) {
      this.router.navigateByUrl('/shunting');
      return;
    }
    if (p.includes('schedule')) {
      this.router.navigateByUrl('/scheduler');
      return;
    }
    if (p.includes('gate')) {
      this.router.navigateByUrl('/gatehouse');
      return;
    }
    if (p.includes('audit')) {
      this.router.navigateByUrl('/audit');
      return;
    }
    
    // Fallback until other persona pages are implemented
    this.router.navigateByUrl('/profile');
  }

  async onLogout(): Promise<void> {
    await this.authService.logout();
  }

  // Values already provided by SessionService
  onNavChange(id: string): void {
    switch ((id || '').toLowerCase()) {
      case 'profile':
        this.router.navigateByUrl('/profile');
        break;
      case 'home':
        {
          // Clear search and filters when navigating to persona home from profile
          console.log('🔄 Navigating to persona home - clearing search and filters');
          this.searchService.clear();
          this.filterService.clear();
          this.uiState.setSearchOpen(false);
          this.uiState.setFiltersOpen(false);
          
          const p = (this.selectedPersona() || '').toLowerCase();
          if (p.includes('grower')) {
            this.router.navigateByUrl('/growers');
            break;
          }
          if (p.includes('transload')) {
            this.router.navigateByUrl('/transloading');
            break;
          }
          if (p.includes('schedule')) {
            this.router.navigateByUrl('/scheduler');
            break;
          }
          if (p.includes('gate')) {
            this.router.navigateByUrl('/gatehouse');
            break;
          }
          if (p.includes('audit')) {
            this.router.navigateByUrl('/audit');
            break;
          }
          this.router.navigateByUrl('/profile');
        }
        break;
      case 'contacts':
        this.router.navigateByUrl('/contacts');
        break;
      default:
        this.router.navigateByUrl('/profile');
    }
  }
}


